import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useCreateClientSetProjectMutation } from "../../../services/campaignmanagement";
import { useClientSetContext } from "./ClientSetContext";

const defaultValues = {
    description: null,
    comment: null,
    adviceType: null,
    status: 0,
    driverId: null,
    targetDate: null,
    revenue: 0,
    messageDriver: false,
    messageAdviser: false,
    messageContent: null
};

const defaultSubject = "'{ProjectDescription}' Project Created for {NumClients} Client(s)";

const useCreateSetProject = ({ onHide }) => {
    const { selectedSet } = useClientSetContext();
    const { clientSetId, setName, setCount } = selectedSet ?? {};

    const { control, watch, setValue: _setValue, clearErrors, handleSubmit, formState: { errors }, reset } = useForm({ defaultValues });
    const setValue = useCallback((name, value) => {
        clearErrors(name);
        _setValue(name, value);
    }, [_setValue, clearErrors])

    const watchDescription = watch("description");
    const [messageDriver, messageAdviser] = watch(["messageDriver", "messageAdviser"]);

    // Display string for previewing message subject
    const messageSubject = useMemo(() => {
        // No recipient = no subject
        if (!messageDriver && !messageAdviser)
            return null;

        const subjectDescription = watchDescription && watchDescription.length > 0
            ? watchDescription
            : "{ProjectDescription}";

        // Only show the number of clients if the recipient is set to driver only
        // If none, no subject will be displayed,
        // If adviser or both, the number of clients in the message will be unknown, so show the placeholder
        const subjectSetCount = setCount != null && messageDriver && !messageAdviser
            ? setCount
            : "{NumClients}";

        return defaultSubject.replace("{ProjectDescription}", subjectDescription)
            .replace("{NumClients}", subjectSetCount);
    }, [messageAdviser, messageDriver, setCount, watchDescription])

    useEffect(() => {
        if (!messageDriver && !messageAdviser)
            setValue("messageContent", null);
    }, [messageAdviser, messageDriver, setValue]);

    const [createProjectTrigger] = useCreateClientSetProjectMutation();

    const onCreateClick = (data) => {
        onHide();
        reset(defaultValues);

        // Remove message subject from the request as this is added to the message backend
        delete data.messageSubject;

        // Remove the message content if the recipient is set to none
        if (!messageDriver && !messageAdviser)
            delete data.messageContent;

        return toast.promise(
            createProjectTrigger({ clientSetId, ...data }).unwrap(),
            {
                loading: "Starting project creation...",
                success: () => "Project creation started",
                error: (err) => {
                    console.error(err);
                    const { data } = err;

                    return data?.message ?? data ?? err?.message ?? "Failed to create project.";
                }
            }
        );
    }

    const onCancel = () => {
        reset(defaultValues);
        onHide();
    }

    return {
        selectedSetName: setName,
        selectedSetCount: setCount,
        driverId: watch("driverId"),
        messageSubject,
        messageDriver,
        messageAdviser,
        control,
        setValue,
        errors,
        onSubmit: handleSubmit(onCreateClick),
        onCancel,
    }
}

export default useCreateSetProject;